import { createApi } from '@reduxjs/toolkit/query/react';
import { DEEP_ANALYSIS_API_URL } from '../settings/config';
import { authRequest } from '../store/authorizedRequests';
import { DomainContentAnalysis, DomainVisibilityAnalysis, DomainContentAnalysisProcess, DomainVisibilityAnalysisProcess, ModelFamily, WebpageVisualSeoAnalysis, WebpageVisualSeoAnalysisProcess } from './types';

const visualSeoActiveProcessId = (wGuid: string, url: string) => `${wGuid}_${url}_visual_seo_analysis`;

// Every assistant has its own analysis, its own process and therefore its own cache entry. The
// assistant belongs in this id: without it, refreshing one assistant makes the others ask the API
// whether an analysis is running, and that question starts one when the domain has none.
const visibilityAnalysisActiveProcessId = (domain: string, modelFamily: ModelFamily) => `${domain}_${modelFamily}_domain_visibility_analysis`;

const llmToolsBaseUrl = `${DEEP_ANALYSIS_API_URL}`;

const RESOURCE_REFETCH_TIME = 7200;

export const llmToolsApi = createApi({
    reducerPath: "api.llm-tools",
    keepUnusedDataFor: RESOURCE_REFETCH_TIME,
    tagTypes: [
        "DomainContentAnalysis",
        "DomainVisibilityAnalysis",
        "WebpageVisualSeoAnalysis",
        "ActiveProcesses",
    ],

    baseQuery: async (args, api, extraOptions) => {
        const { url, method, body, signal } = args;
        const { dispatch, getState } = api;

        try {
            const data = await authRequest(llmToolsBaseUrl.concat(url), dispatch, getState, {
                method: method,
                body: JSON.stringify(body),
                signal: signal,
            });

            return { data };
        } catch (error) {
            return { error };
        }
    },

    endpoints: (builder) => ({
        fetchDomainContentAnalysis: builder.query<DomainContentAnalysis, { domain: string }>({
            query: (args) => {
                const { domain } = args;

                return {
                    method: 'GET',
                    url: `/content-analysis?domain=${encodeURIComponent(domain)}`,
                };
            },
            providesTags: ['DomainContentAnalysis'],
        }),

        createDomainContentAnalysisProcess: builder.mutation<{ guid: string }, { domain: string }>({
            query: (args) => {
                return {
                    method: 'POST',
                    body: args,
                    url: '/content-analysis',
                };
            },

            invalidatesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: `${args.domain}_domain_content_analysis` }],
        }),

        fetchDomainContentAnalysisProcess: builder.query<DomainContentAnalysisProcess, { domain: string, processGuid: string | null }>({
            query: (args) => {
                const { domain, processGuid } = args;
                const endpoint = processGuid ? `/content-analysis/active?domain=${encodeURIComponent(domain)}&process_guid=${processGuid}` : `/content-analysis/active?domain=${encodeURIComponent(domain)}`;
                return {
                    method: 'GET',
                    url: endpoint,
                };
            },

            providesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: `${args.domain}_domain_content_analysis` }],
        }),

        fetchDomainVisibilityAnalysis: builder.query<DomainVisibilityAnalysis, { domain: string, modelFamily: ModelFamily }>({
            query: (args) => {
                const { domain, modelFamily } = args;

                return {
                    method: 'GET',
                    url: `/visibility-analysis?domain=${encodeURIComponent(domain)}&model_family=${modelFamily}`,
                };
            },
            providesTags: (result, error, args) => [{ type: 'DomainVisibilityAnalysis', id: args.modelFamily }]
        }),

        createDomainVisibilityAnalysisProcess: builder.mutation<{ guid: string }, { domain: string, modelFamily: ModelFamily }>({
            query: (args) => {
                const { domain, modelFamily } = args;

                // Spelled out rather than forwarded: the API reads `model_family`, so sending the
                // arguments as they stand would drop the assistant and analyse with the default one.
                return {
                    method: 'POST',
                    body: { domain, model_family: modelFamily },
                    url: '/visibility-analysis',
                };
            },

            invalidatesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: visibilityAnalysisActiveProcessId(args.domain, args.modelFamily) }],
        }),

        fetchDomainVisibilityAnalysisProcess: builder.query<DomainVisibilityAnalysisProcess, { domain: string, modelFamily: ModelFamily, processGuid: string | null }>({
            query: (args) => {
                const { domain, modelFamily, processGuid } = args;
                const baseUrl = `/visibility-analysis/active?domain=${encodeURIComponent(domain)}&model_family=${modelFamily}`;
                const endpoint = processGuid ? `${baseUrl}&process_guid=${processGuid}` : baseUrl;
                return {
                    method: 'GET',
                    url: endpoint,
                };
            },

            providesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: visibilityAnalysisActiveProcessId(args.domain, args.modelFamily) }],
        }),

        fetchWebpageVisualSeoAnalysis: builder.query<WebpageVisualSeoAnalysis, { wGuid: string, url: string }>({
            query: (args) => {
                const { wGuid, url } = args;

                return {
                    method: 'GET',
                    url: `/visual-seo-analysis?w_guid=${encodeURIComponent(wGuid)}&url=${encodeURIComponent(url)}`,
                };
            },
            providesTags: ['WebpageVisualSeoAnalysis'],
        }),

        createWebpageVisualSeoAnalysisProcess: builder.mutation<{ guid: string, c_at: string }, { wGuid: string, url: string }>({
            query: (args) => {
                const { wGuid, url } = args;

                return {
                    method: 'POST',
                    body: { w_guid: wGuid, url },
                    url: '/visual-seo-analysis',
                };
            },

            invalidatesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: visualSeoActiveProcessId(args.wGuid, args.url) }],
        }),

        fetchWebpageVisualSeoAnalysisProcess: builder.query<WebpageVisualSeoAnalysisProcess, { wGuid: string, url: string, processGuid: string | null }>({
            query: (args) => {
                const { wGuid, url, processGuid } = args;
                const baseUrl = `/visual-seo-analysis/active?w_guid=${encodeURIComponent(wGuid)}&url=${encodeURIComponent(url)}`;
                const endpoint = processGuid ? `${baseUrl}&process_guid=${processGuid}` : baseUrl;
                return {
                    method: 'GET',
                    url: endpoint,
                };
            },

            providesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: visualSeoActiveProcessId(args.wGuid, args.url) }],
        }),

    })
});

export const {
    useFetchDomainContentAnalysisQuery,
    useLazyFetchDomainContentAnalysisQuery,
    useFetchDomainContentAnalysisProcessQuery,
    useCreateDomainContentAnalysisProcessMutation,

    useFetchDomainVisibilityAnalysisQuery,
    useLazyFetchDomainVisibilityAnalysisQuery,
    useFetchDomainVisibilityAnalysisProcessQuery,
    useCreateDomainVisibilityAnalysisProcessMutation,

    useFetchWebpageVisualSeoAnalysisQuery,
    useLazyFetchWebpageVisualSeoAnalysisQuery,
    useCreateWebpageVisualSeoAnalysisProcessMutation,
    useFetchWebpageVisualSeoAnalysisProcessQuery,
} = llmToolsApi;

